import { useMemo, useState } from "react";
import BookExtractsSearch from "../BookExtractsSearch/BookExtractsSearch";
import BookSortControl from "../BookSortControl/BookSortControl";
import BookTable from "../BookTable/BookTable";
import BookCard from "../BookCard/BookCard";
import { bookExtractHeaders } from "../BookTable/headers";
import { sortItems } from "../../utils/sort";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useBookExtracts } from "./useBookExtracts";
import type { Book, SortState } from "./types";
import "./BookExtracts.scss";

// Three-stage sort cycle
// default is null (which is original order), first click does ascendening, second click descending
// final is the default order
export const cycleSortState = (
  current: SortState | null,
  key: string,
): SortState | null => {
  if (!current || current.key !== key) {
    return { key, direction: "asc" };
  }
  if (current.direction === "asc") {
    return { key, direction: "desc" };
  }
  return null;
};

function BookExtracts() {
  const [query, setQuery] = useState("");
  // Debounce the query to avoid multiple requests and fetch only when user pauses typing
  const debouncedQuery = useDebouncedValue(query, 300);
  const { books, isLoading, error } = useBookExtracts(debouncedQuery);
  const [sort, setSort] = useState<SortState | null>(null);

  const sortedBooks = useMemo(() => {
    if (!sort) {
      return books;
    }
    const header = bookExtractHeaders.find((item) => item.key === sort.key);
    return sortItems<Book>(
      books,
      sort.key as keyof Book,
      sort.direction,
      header?.sortType ?? "string",
    );
  }, [books, sort]);

  const handleHeaderSort = (key: string) => {
    setSort((current) => cycleSortState(current, key));
  };

  return (
    <main className="book-extracts">
      <div className="container">
        <BookExtractsSearch value={query} onChange={setQuery} />
        <BookSortControl sort={sort} onSort={handleHeaderSort} />

        {
          // error case
          error && (
            <p className="book-extracts__message book-extracts__message--error">
              {error}
            </p>
          )
        }

        {
          // Loading case (no error, loading without results)
          !error && isLoading && books.length === 0 && (
            <p className="book-extracts__message">Loading book extracts…</p>
          )
        }

        {
          // no results (no error, not loading and no results)
          !error && !isLoading && books.length === 0 && (
            <p className="book-extracts__message">
              No book extracts match your search.
            </p>
          )
        }

        {!error && books.length > 0 && (
          <div
            className={`book-extracts__results${
              isLoading ? " book-extracts__results--loading" : ""
            }`}
          >
            <div className="book-cards">
              {sortedBooks.map((book, index) => (
                <BookCard key={book.isbn} book={book} index={index} />
              ))}
            </div>

            <BookTable
              books={sortedBooks}
              sort={sort}
              onSort={handleHeaderSort}
            />
          </div>
        )}
      </div>
    </main>
  );
}

export default BookExtracts;
